<template>
  <section
    class="c-home-search-concerts-root v-page-section-container-padding col-span-full relative z-20 container mx-auto md:flex lg:grid grid-cols-2 2xl:grid-cols-12 gap-5 lg:gap-8"
    :class="{ 'is-search-active': isSearchActive || isElevated }"
  >
    <transition name="fade">
      <div v-if="isSearchActive" class="c-home-search-overlay fixed inset-0 z-30" aria-hidden="true" @click="onOverlayClick" @touchend.prevent="onOverlayClick" />
    </transition>

    <div class="c-home-search-card relative z-20 bg-white mb-5 md:mb-0 2xl:col-span-5 2xl:col-start-2" :class="{ 'is-elevated': isSearchActive || isElevated }">
      <div class="c-home-search-fields-wrap">
        <ul class="c-home-search-fields">
          <li class="c-home-search-field c-home-search-field--keyword">
            <h2 class="c-home-search-heading">
              {{ $t('home-search-input-title') }}
            </h2>
            <div class="relative">
              <input
                ref="searchInputEl"
                v-model="concertSearchInput"
                type="text"
                :placeholder="`${$t('home-search-input-placeholder-v1')}`"
                class="c-home-search-input"
                :class="{ 'is-keyboard-focused': searchInputKeyboardFocus }"
                autocomplete="off"
                role="combobox"
                aria-autocomplete="list"
                :aria-expanded="showSuggestions ? 'true' : 'false'"
                :aria-activedescendant="activeSuggestionId"
                @input="onSearchInput"
                @focus="onSearchFocus"
                @keydown="onSearchKeydown"
                @blur="onSearchBlur"
              />
              <AtomIconSearch class="c-home-search-input__icon absolute top-1/2 right-4 cursor-pointer" tabindex="-1" @click="commitInputSearch" />
            </div>
          </li>
          <li class="c-home-search-field c-home-search-field--date">
            <h2 class="c-home-search-heading">
              {{ $t('home-date-input-title') }}
            </h2>
            <!--
              New datepicker kept for later sprint rollout.
            <MoleculeDatepicker
              background-color="bg-app-primary-grey--black-squeeze"
              text-color="text-black"
              icon-color="text-black"
              size="large"
              :enable-icon-left="false"
              :shadowed="false"
              :justified="true"
              :icon-size="4"
              class-list="c-home-search-datepicker w-full"
              @set-date="commitDatePickerSearch"
            />
            -->
            <MoleculeDatepickerLegacy
              background-color="bg-app-primary-grey--black-squeeze"
              text-color="text-black"
              icon-color="text-black"
              size="large"
              :enable-icon-left="false"
              :shadowed="false"
              :justified="true"
              :icon-size="4"
              class-list="c-home-search-datepicker w-full"
              @set-date="commitDatePickerSearch"
            />
          </li>
        </ul>
        <ul v-show="showSuggestions" id="home-search-suggestions" class="c-home-search-suggestions" role="listbox" @focusout="onSuggestionsFocusOut">
          <li v-for="(suggestion, index) in suggestions" :key="`${suggestion.type}-${suggestion.label}`" role="presentation">
            <button
              :id="`home-search-suggestion-${index}`"
              type="button"
              role="option"
              class="c-home-search-suggestion"
              :class="{ 'is-active': activeSuggestionIndex === index }"
              :aria-selected="activeSuggestionIndex === index ? 'true' : 'false'"
              @mousedown.prevent
              @click="selectSuggestion(suggestion)"
            >
              <span class="c-home-search-suggestion__label">{{ suggestion.label }}</span>
              <span class="c-home-search-suggestion__type">{{ suggestion.type }}</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
    <div v-if="recommendationUrl" class="relative z-10 bg-white rounded-3xl px-7 sm:px-9 md:px-5 xl:px-12 py-5 sm:py-10 2xl:col-span-5">
      <h2 class="text-black mb-4 xl:mb-6 text-xl sm:text-base md:text-base lg:text-lg xl:text-xl font-medium leading-none">
        {{ $t('home-search-help-title') }}
      </h2>
      <MoleculeButton class="w-full xl:hidden" size="large" background-color="bg-app-primary-1-red--fire-brick hover:bg-black" @click="startQuestionnaire">{{
        $t('home-search-help-button')
      }}</MoleculeButton>
      <MoleculeButton class="hidden w-full xl:flex" background-color="bg-app-primary-1-red--fire-brick hover:bg-black" @click="startQuestionnaire">{{
        $t('home-search-help-button')
      }}</MoleculeButton>
    </div>
  </section>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'
import { toLocalDateString } from '~/assets/util'
import { explodeUrl } from '@/assets/link'

const SUGGESTION_LIMIT_DESKTOP = 10
const SUGGESTION_LIMIT_MOBILE = 8
const OVERLAY_FADE_MS = 250

const typeToFacetKey: Record<string, string> = {
  Componist: 'composers',
  Korting: 'discounts',
  Concert: 'concertTitles',
  Genre: 'genres',
  Instrument: 'instruments',
  Zaal: 'rooms',
  Concertreeks: 'specials',
  Organisator: 'organizers',
  Concerttitel: 'concertTitles',
  Musici: 'musicians',
  Werken: 'works',
}

const props = defineProps({
  recommendationUrl: { type: String, required: false, default: undefined },
  genres: { type: Array as PropType<any[]>, required: false, default: () => [] },
})

const router = useRouter()
const globalStore = useGlobalStore()
const eventOverviewStore = useEventOverviewStore()
const recommendationsStore = useRecommendationsStore()
const concertSearchInput = ref('')
const searchInputEl = ref<HTMLInputElement | null>(null)
const suggestionsOpen = ref(false)
const searchInputKeyboardFocus = ref(false)
const activeSuggestionIndex = ref(-1)
const isSearchActive = ref(false)
const isElevated = ref(false)
let focusCameFromKeyboard = false
let elevateTimeout: ReturnType<typeof setTimeout> | null = null
let pendingCloseMethod: string | null = null

const registerGtmSuggestionSelect = setupGtmSuggestionSelect()
const registerGtmSearchFocusOpen = setupGtmSearchFocusOpen()
const registerGtmSearchFocusClose = setupGtmSearchFocusClose()
const registerGtmSearchViewAllResults = setupGtmSearchViewAllResults()

const cancelElevateTimeout = () => {
  if (elevateTimeout) {
    clearTimeout(elevateTimeout)
    elevateTimeout = null
  }
}

watch(isSearchActive, (active) => {
  cancelElevateTimeout()
  if (active) {
    isElevated.value = true
  } else {
    elevateTimeout = setTimeout(() => {
      if (!isSearchActive.value) {
        isElevated.value = false
      }
      elevateTimeout = null
    }, OVERLAY_FADE_MS)
  }
})

const suggestionLimit = () => (typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches ? SUGGESTION_LIMIT_MOBILE : SUGGESTION_LIMIT_DESKTOP)

const fetchSuggestionsDebounced = debounce((q) => {
  eventOverviewStore.fetchKeywordSuggestions({ q, limit: suggestionLimit() })
}, 300)

const suggestions = computed(() => {
  const apiSuggestions = eventOverviewStore.keywordSuggestions || []
  return apiSuggestions.slice(0, suggestionLimit())
})

const showSuggestions = computed(() => suggestionsOpen.value && suggestions.value.length > 0)

const activeSuggestionId = computed(() => {
  if (activeSuggestionIndex.value < 0 || !showSuggestions.value) {
    return undefined
  }
  return `home-search-suggestion-${activeSuggestionIndex.value}`
})

const agendaPath = () => explodeUrl(globalStore.eventOverview?.url || '').path

const clearSuggestions = () => {
  suggestionsOpen.value = false
  activeSuggestionIndex.value = -1
  fetchSuggestionsDebounced.cancel()
  eventOverviewStore.setKeywordSuggestions([])
}

const deactivateSearch = () => {
  if (isSearchActive.value) {
    const method = pendingCloseMethod
    pendingCloseMethod = null
    registerGtmSearchFocusClose(method || undefined)
  }
  pendingCloseMethod = null
  isSearchActive.value = false
  searchInputKeyboardFocus.value = false
  clearSuggestions()
  searchInputEl.value?.blur?.()
}

const goToAgenda = (query: CmsData) => {
  deactivateSearch()
  router.push({ path: agendaPath(), query })
}

const onOverlayClick = () => {
  pendingCloseMethod = 'click_outside'
  deactivateSearch()
}

const onFocusModalityKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Tab') {
    focusCameFromKeyboard = true
  }
  if (event.key === 'Escape' && isSearchActive.value) {
    event.preventDefault()
    pendingCloseMethod = 'escape'
    deactivateSearch()
  }
}

const onFocusModalityPointerDown = () => {
  focusCameFromKeyboard = false
  searchInputKeyboardFocus.value = false
}

const onSearchFocus = () => {
  if (!isSearchActive.value) {
    registerGtmSearchFocusOpen()
  }
  isSearchActive.value = true
  if (focusCameFromKeyboard) {
    searchInputKeyboardFocus.value = true
  }
  focusCameFromKeyboard = false
}

const onSearchInput = () => {
  const trimmed = concertSearchInput.value.trim()
  suggestionsOpen.value = trimmed.length > 0
  activeSuggestionIndex.value = -1
  if (trimmed) {
    fetchSuggestionsDebounced(trimmed)
  } else {
    clearSuggestions()
  }
}

const commitInputSearch = () => {
  registerGtmSearchViewAllResults()
  // Set a flag so the agenda page fires search_performed with the real result count
  // once the search has settled. Firing here would always yield result_count: 0
  // because the event-overview store has not been populated on the homepage yet.
  eventOverviewStore.setPendingSearchPerformed(true)
  goToAgenda({ keyword: concertSearchInput.value || undefined })
}

const selectSuggestion = (suggestion: CmsData) => {
  activeSuggestionIndex.value = -1
  const facetKey = suggestion.facet || typeToFacetKey[suggestion.type]
  const suggestionValue = suggestion.value || suggestion.label

  // Fire suggestion_select with the facet key and server-side matchRank.
  registerGtmSuggestionSelect({
    suggestionType: facetKey || suggestion.type,
    matchRank: suggestion.matchRank ?? 0,
  })

  if (facetKey && suggestionValue) {
    setGtmFilterSelectClickLocation('Suggestion')
    eventOverviewStore.setPendingSearchPerformed(true)
    goToAgenda({ [facetKey]: suggestionValue })
    return
  }

  eventOverviewStore.setPendingSearchPerformed(true)
  goToAgenda({ keyword: suggestion.label || undefined })
}

const onSearchKeydown = (event: KeyboardEvent) => {
  const count = suggestions.value.length
  const listOpen = showSuggestions.value && count > 0

  if (event.key === 'ArrowDown' && listOpen) {
    event.preventDefault()
    activeSuggestionIndex.value = activeSuggestionIndex.value < count - 1 ? activeSuggestionIndex.value + 1 : 0
    return
  }

  if (event.key === 'ArrowUp' && listOpen) {
    event.preventDefault()
    activeSuggestionIndex.value = activeSuggestionIndex.value <= 0 ? count - 1 : activeSuggestionIndex.value - 1
    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    if (listOpen && activeSuggestionIndex.value >= 0) {
      selectSuggestion(suggestions.value[activeSuggestionIndex.value])
      return
    }
    commitInputSearch()
  }
}

const onSearchBlur = () => {
  setTimeout(() => {
    const active = document.activeElement
    if (active instanceof Element && active.closest('.c-home-search-suggestions')) {
      return
    }
    if (active !== searchInputEl.value) {
      searchInputKeyboardFocus.value = false
      activeSuggestionIndex.value = -1
      isSearchActive.value = false
    }
    suggestionsOpen.value = false
  }, 150)
}

const onSuggestionsFocusOut = () => {
  setTimeout(() => {
    const active = document.activeElement
    if (active instanceof Element && active.closest('.c-home-search-suggestions')) {
      return
    }
    if (active === searchInputEl.value) {
      return
    }
    suggestionsOpen.value = false
    activeSuggestionIndex.value = -1
    isSearchActive.value = false
  }, 0)
}

const commitDatePickerSearch = (date: Date) => {
  goToAgenda({ date: toLocalDateString(date) })
}

const startQuestionnaire = () => {
  recommendationsStore.reset()
  const path = explodeUrl(props.recommendationUrl || '').path
  router.push({ path })
}

onMounted(() => {
  window.addEventListener('keydown', onFocusModalityKeyDown, true)
  window.addEventListener('mousedown', onFocusModalityPointerDown, true)
  window.addEventListener('touchstart', onFocusModalityPointerDown, true)
})

onBeforeUnmount(() => {
  cancelElevateTimeout()
  fetchSuggestionsDebounced.cancel()
  window.removeEventListener('keydown', onFocusModalityKeyDown, true)
  window.removeEventListener('mousedown', onFocusModalityPointerDown, true)
  window.removeEventListener('touchstart', onFocusModalityPointerDown, true)
})
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.c-home-search-concerts-root.is-search-active {
  z-index: 40;
}

.c-home-search-overlay {
  background-color: rgba(0, 0, 0, 0.3);
}

.c-home-search-card.is-elevated {
  z-index: 40;
}

.c-home-search-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 38px 25px;
  gap: 5px;
  border-radius: 24px;
}

.c-home-search-fields-wrap {
  position: relative;
  width: 100%;
}

.c-home-search-fields {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0;
  gap: 12px;
  width: 100%;

  @screen sm {
    flex-direction: row;
    align-items: flex-start;
  }
}

.c-home-search-field {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0;
  gap: 24px;
  width: 100%;
  min-width: 0;

  &--keyword {
    @screen sm {
      flex: 1 1 auto;
    }
  }

  &--date {
    @screen sm {
      flex: 0 0 auto;
      width: 192px;
    }
  }
}

.c-home-search-heading {
  @apply text-black;
  margin: 0;
  width: 100%;
  font-family: FuturaTL, Helvetica, Arial, sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 20px;
}

.c-home-search-input {
  @apply w-full bg-app-primary-grey--black-squeeze;
  box-sizing: border-box;
  padding: 12px 40px 12px 16px;
  min-height: 42px;
  height: 42px;
  border-radius: 30px;
  outline: none;
  font-family: FuturaTL, Helvetica, Arial, sans-serif;
  font-style: italic;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #303030;

  &.is-keyboard-focused {
    outline: 5px auto -webkit-focus-ring-color;
    outline-offset: 0;
  }

  &::placeholder {
    font-family: FuturaTL, Helvetica, Arial, sans-serif;
    font-style: italic;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: #303030;
  }
}

.c-home-search-input__icon {
  width: 14px;
  height: 14px;
  margin-top: -7px;
  color: theme('colors.app.primary-1-red--fire-brick');
}

.c-home-search-field--date {
  :deep(.c-home-search-datepicker) {
    box-sizing: border-box;
    justify-content: space-between;
    padding: 12px 32px;
    gap: 30px;
    min-height: 40px;
    height: 40px;
    border-radius: 24px;
    font-family: FuturaTL, Helvetica, Arial, sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 16px;
  }

  :deep(.c-home-search-datepicker svg) {
    margin-left: 0;
    color: theme('colors.black');
  }
}

.c-home-search-suggestions {
  @apply absolute left-0 right-0 flex flex-col bg-white;
  top: calc(20px + 24px + 42px + 8px);
  z-index: 50;
  padding: 0;
  border: 0.5px solid rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  box-shadow:
    0px 10px 15px -3px rgba(0, 0, 0, 0.1),
    0px 4px 6px -4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.c-home-search-suggestion {
  @apply flex w-full items-center justify-between gap-2 text-left;
  padding: 12px 16px;
  color: #303030;
  font-family: FuturaTL, Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;

  &:hover,
  &:focus,
  &.is-active {
    outline: none;
    background-color: theme('colors.app.primary-grey--black-squeeze');

    .c-home-search-suggestion__label {
      color: theme('colors.app.primary-1-red--fire-brick');
      font-weight: 600;
    }
  }
}

.c-home-search-suggestion__label {
  @apply min-w-0;
  color: rgba(48, 48, 48, 1);
  font-family: FuturaTL, Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
}

.c-home-search-suggestion__type {
  @apply shrink-0 text-right;
  color: rgba(86, 70, 72, 1);
  font-family: FuturaTL, Helvetica, Arial, sans-serif;
  font-weight: 600;
  font-style: italic;
  font-size: 14px;
  line-height: 100%;
}
</style>
